import VideoPlayerBackground from "./VideoPlayerBackground";
import Swiper, { FreeMode } from "swiper";
Swiper.use([FreeMode]);

export default class VideoPlayer {
    constructor() {
        this.currentVideoId = null;
        this.player = null;
        this.theaterModeTimer = null;
        this.isTheaterMode = false;
        this.isSlider = this.isPlaylistSlider()
        this.iframeLocked = null;
        this.isPlaying = false;
        this.hasInteracted = false;
        this.playerIsReseted = false;

        this.initElements();

        if(this.isSlider) {
            this.initSwiper()
        }

        this.initBackground();
        this.initYouTubeAPI();
        this.initEventListeners();

        if(!this.placeholderImgEl) {
            this.initPlaceholder();
        }
    }

    isPlaylistSlider() {
        return window.matchMedia("(max-width: 1024px)").matches
    }

    initElements() {
        this.container = document.querySelector('.gm2-player');
        this.playerContainer = document.querySelector('#video-player-container');
        this.playlist = document.querySelector('.playlist .video-item-list');
        this.playlistButtons = [...this.playlist.querySelectorAll('.video-item')];
        this.mobileSliderEl = document.querySelector('.video-item-list');

        // Video details elements
        this.titleElement = document.querySelector('#video-player-title');
        this.authorElement = document.querySelector('#video-player-author');
        this.ctaElement = document.querySelector('#video-player-cta');

        this.initialVideoId = this.playerContainer.dataset.mainVideoId;

        this.videoControls = this.playerContainer.querySelector('.video-controls');
        this.playButton = this.videoControls.querySelector('.play-button');
        this.iframe = this.playerContainer.querySelector('.iframe');
        this.placeholderEl = this.playerContainer.querySelector('.video-placeholder');
        this.placeholderImgEl = this.placeholderEl.querySelector('img');
    }

    initYouTubeAPI() {
        if (!window.YT) {
            const tag = document.createElement('script');
            tag.src = 'https://www.youtube.com/iframe_api';
            const firstScriptTag = document.getElementsByTagName('script')[0];
            firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
        }

        if (window.YT && window.YT.Player) {
            console.log('YT.Player already loaded');
            this.createPlayer(this.initialVideoId);
        } else {
            window.onYouTubeIframeAPIReady = () => {
                this.createPlayer(this.initialVideoId);
            };
        }
    }

    initPlaceholder(newVideoId = null) {
        const videoId = newVideoId || this.initialVideoId;

        const thumbnailUrls = [
            `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
            `https://img.youtube.com/vi/${videoId}/sddefault.jpg`,
            `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
            `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`,
            `https://img.youtube.com/vi/${videoId}/default.jpg`
        ];

        this.loadFirstAvailableThumbnail(thumbnailUrls);
    }

    loadFirstAvailableThumbnail(urls) {
        if (urls.length === 0) return;

        const img = new Image();
        img.onload = () => {
            if (img.width !== 120 && img.height !== 90) {
                if(this.placeholderImgEl) {
                    this.placeholderImgEl.src = img.src;
                } else {
                    this.placeholderImgEl = document.createElement('img');
                    this.placeholderImgEl.src = img.src;
                    this.placeholderEl.appendChild(this.placeholderImgEl);
                }
            } else {
                this.loadFirstAvailableThumbnail(urls.slice(1));
            }
        };
        img.onerror = () => {
            this.loadFirstAvailableThumbnail(urls.slice(1));
        };
        img.src = urls[0];
    }

    createPlayer(videoId) {
        this.player = new YT.Player('video-player', {
            height: '360',
            width: '640',
            videoId: videoId,
            playerVars: {
                'autoplay': 0,
                'controls': 1,
                'showinfo': 0,
                'rel': 0,
            },
            events: {
                'onStateChange': this.onPlayerStateChange.bind(this)
            }
        });

        this.iframe = this.player.getIframe();

        this.currentVideoId = videoId;
    }

    initEventListeners() {
        // Playlist click handlers
        this.playlistButtons.forEach(button => {
            button.addEventListener('click', (e) => {
                e.preventDefault();
                const videoId = button.dataset.ytVideoId;
                const videoDetails = {
                    title: button.dataset.title,
                    author: button.dataset.author,
                    cta: button.dataset.cta,
                    placeholder: button.dataset.placeholder
                };
                this.loadVideo(videoId, videoDetails);
            });
        });

        // Theater mode exit handlers
        document.addEventListener('mousemove', this.handleUserActivity.bind(this));
        document.addEventListener('scroll', this.handleUserActivity.bind(this));
        window.addEventListener('touchstart', this.lockIframe.bind(this), { capture: true });
        window.addEventListener('scroll', this.lockIframe.bind(this), { capture: true });
        window.addEventListener('wheel', this.lockIframe.bind(this), { capture: true });


        this.videoControls.addEventListener('click', (e) => {
            if (e.target === this.videoControls) {
                this.togglePlay();
            }
        });

        this.playButton.addEventListener('click', () => {
            this.togglePlay();
        });

        // Handle focus events
        this.playerContainer.addEventListener('mouseenter', () => {
            if (this.isPlaying) {
                // this.showControls();
            }
        });

        this.playerContainer.addEventListener('mouseleave', () => {
            if (this.isPlaying) {
                // this.hideControls();
            }
        });
    }

    showControls() {
        this.videoControls.classList.remove('hidden');
    }

    hideControls() {
        this.videoControls.classList.add('hidden');
    }

    showPlayButton() {
        this.playButton.classList.remove('hidden');
    }

    hidePlayButton() {
        this.playButton.classList.add('hidden');
    }

    togglePlay() {
        if (!this.player) return;

        if (!this.hasInteracted) {
            this.hasInteracted = true;
            this.placeholderEl.classList.add('hidden');
        }

        if (this.isPlaying) {
            this.player.pauseVideo();
            this.isPlaying = false;
            this.showControls();
            this.iframe.classList.remove('active');
        } else {
            this.player.playVideo();
            this.isPlaying = true;
            this.hideControls();
            this.hidePlayButton();
            this.iframe.classList.add('active');
            // User manually played the video, so mark as not reset
            this.playerIsReseted = false;
        }
    }

    lockIframe() {
        //console.log('lockIframe', this.iframeLocked);
        if (!this.iframeLocked) {
            this.iframe.style.pointerEvents = 'none';
            // console.log('Locked');
            // console.log(this.iframe);
        }

        clearTimeout(this.iframeLocked);
        this.iframeLocked = setTimeout(() => {
            this.iframeLocked = null;
            this.iframe.style.pointerEvents = 'auto';
            // console.log('Unlocked');
        }, 1000);
    }

    updateVideoDetails(details) {
        if (details.title && this.titleElement) {
            this.titleElement.textContent = details.title;
        }

        if (details.author && this.authorElement) {
            this.authorElement.innerHTML = details.author;
        }

        if (details.cta && this.ctaElement) {
            const ctaLink = this.ctaElement.querySelector('.link');
            if (ctaLink) {
                ctaLink.href = details.cta;
            }
        }

        // Handle placeholder image
        if (details.placeholder && details.placeholder.trim() !== '' && this.placeholderImgEl) {
            this.placeholderImgEl.src = details.placeholder;
            // Ensure placeholder is visible when updating
            this.placeholderEl.classList.remove('hidden');
        } else if (this.placeholderImgEl) {
            // If no placeholder provided or empty, initialize with YouTube thumbnail
            const videoId = this.currentVideoId || this.initialVideoId;
            this.initPlaceholder(videoId);
            this.placeholderEl.classList.remove('hidden');
        }
    }

    loadVideo(videoId, videoDetails = null) {
        // Skip if same video is already loaded
        if (this.currentVideoId === videoId) return;

        if (this.player && this.player.loadVideoById) {
            this.player.cueVideoById(videoId);
            this.currentVideoId = videoId;
            this.showControls();
            this.showPlayButton();
            this.isPlaying = false;
            this.placeholderEl.classList.remove('hidden');
            this.iframe.classList.remove('active');
            
            // Mark as not reset when loading a new video
            this.playerIsReseted = false;

            // Update video details if provided
            if (videoDetails) {
                this.updateVideoDetails(videoDetails);
            }
        }
    }

    hidePlaceholder() {
        this.placeholderEl.classList.add('hidden');
    }

    onPlayerStateChange(event) {
        // YT.PlayerState.PLAYING = 1
        this.isPlaying = event.data === 1;

        if (this.isPlaying) {
            this.hidePlaceholder();
            this.hideControls();
            this.startTheaterModeTimer();
            // User has started playing, so mark as not reset
            this.playerIsReseted = false;
        } else {
            this.showControls();
            this.clearTheaterModeTimer();
        }
    }

    startTheaterModeTimer() {
        this.clearTheaterModeTimer();
        this.theaterModeTimer = setTimeout(() => {
            this.setTheaterMode();
        }, 10000); // 10 seconds
    }

    clearTheaterModeTimer() {
        if (this.theaterModeTimer) {
            clearTimeout(this.theaterModeTimer);
            this.theaterModeTimer = null;
        }
    }

    handleUserActivity() {
        if (this.isTheaterMode) {
            this.setNormalMode();
        }
    }

    initBackground() {
        this.background = new VideoPlayerBackground();
    }

    setTheaterMode() {
        if (!this.isTheaterMode) {
            this.isTheaterMode = true;
            this.container.classList.add('theater-mode');
            this.background.pause();
        }
    }

    setNormalMode() {
        if (this.isTheaterMode) {
            this.isTheaterMode = false;
            this.container.classList.remove('theater-mode');
            this.background.resume();

            // Restart theater mode timer if video is still playing
            if (this.player && this.player.getPlayerState() === 1) {
                this.startTheaterModeTimer();
            }
        }
    }

    initSwiper() {
        if(!this.mobileSliderEl) return
        // add swiper-wrapper element
        this.mobileSliderEl.innerHTML = `<div class="swiper-wrapper">${this.mobileSliderEl.innerHTML}</div>`;

        this.mobileSlider = new Swiper(this.mobileSliderEl, {
            slidesPerView: 2.5,
            spaceBetween: 10,
            speed: 1000,
            initialSlide: 1,
            loop: true,
            freeMode: true,
            on: {
                click: (swiper, e ) => {
                    const button = e.target.closest('.thumb');
                    const videoId = button.dataset.ytVideoId;
                    const videoDetails = {
                        title: button.dataset.title,
                        author: button.dataset.author,
                        cta: button.dataset.cta
                    };
                    this.loadVideo(videoId, videoDetails);
                }
            },
            breakpoints: {
                767: {
                    slidesPerView: 3.5,
                    spaceBetween: 20,
                }
            }
        })
    }

    resetVideoPlayer() {
        // If already reset, don't do it again
        if (this.playerIsReseted) {
            console.log('Player already reset, skipping');
            return;
        }
        
        console.log('resetVideoPlayer');
        
        // Check if the player is fully initialized
        if (!this.player || typeof this.player.seekTo !== 'function') {
            console.log('Player not fully initialized yet, marking as reset');
            this.playerIsReseted = true;
            return;
        }
        
        // Reset to the first video in the playlist
        if (this.playlistButtons && this.playlistButtons.length > 0) {
            const firstVideoButton = this.playlistButtons[0];
            
            // If current video is already the first one, just reset it to the beginning
            if (this.currentVideoId === firstVideoButton.dataset.ytVideoId && this.player) {
                this.player.seekTo(0);
                this.player.pauseVideo();
                this.isPlaying = false;
                this.showControls();
                this.showPlayButton();
                this.placeholderEl.classList.remove('hidden');
                this.iframe.classList.remove('active');
                this.playerIsReseted = true;
                return;
            }
            
            // Otherwise load the first video
            const videoId = firstVideoButton.dataset.ytVideoId;
            const videoDetails = {
                title: firstVideoButton.dataset.title,
                author: firstVideoButton.dataset.author,
                cta: firstVideoButton.dataset.cta,
                placeholder: firstVideoButton.dataset.placeholder
            };
            this.loadVideo(videoId, videoDetails);
            this.playerIsReseted = true;
        } else if (this.initialVideoId && this.player) {
            // Fallback to initial video if no playlist is available
            this.player.seekTo(0);
            this.player.pauseVideo();
            this.isPlaying = false;
            this.showControls();
            this.showPlayButton();
            if (this.placeholderEl) {
                this.placeholderEl.classList.remove('hidden');
            }
            if (this.iframe) {
                this.iframe.classList.remove('active');
            }
            
            // If current video is not the initial one, load it
            if (this.currentVideoId !== this.initialVideoId) {
                this.loadVideo(this.initialVideoId);
            }
            this.playerIsReseted = true;
        }
    }

    // Add method to mark player as not reset
    markPlayerAsNotReset() {
        this.playerIsReseted = false;
    }

    // Cleanup method
    destroy() {
        this.clearTheaterModeTimer();
        document.removeEventListener('mousemove', this.handleUserActivity.bind(this));
        document.removeEventListener('scroll', this.handleUserActivity.bind(this));
        if (this.player && this.player.destroy) {
            this.player.destroy();
        }
    }
}
